<script setup>
import { ref, onMounted, getCurrentInstance, inject } from "vue";
import { useRouter, RouterLink } from "vue-router";
import adminPartnerService from "../../services/partner/adminPartnerService";
import SNBAdmin from "../../../../layouts/nav/SNBAdmin.vue";
import { usePageLoadingStore } from "../../../../stores/pageLoading";
import KSASecondaryButton from "../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSAPrimaryButton from "../../../../components/ui-components/button/KSAPrimaryButton.vue";

const router = useRouter();

const loadingStore = usePageLoadingStore();
const { $pageListBoardNo, $toast, $number, $yyyymmddHHmmss, $yyyymmdd } = getCurrentInstance().appContext.config.globalProperties;

onMounted(() => {
    retrieveList();
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);
const searchPageSizeRef = ref(null);
const searchOpen = ref(true);

const list = ref([]);

// 검색 조건
const _searchInput = ref({
    searchPartnerName: '', // 담당기관명
});

const pageSizeGroup = ref([
    { label: "10개씩 보기", value: "10" },
    { label: "30개씩 보기", value: "30" },
    { label: "50개씩 보기", value: "50" },
    { label: "100개씩 보기", value: "100" }
]);

const retrieveList = async () => {
    let paramObj = {
        searchPartnerName: _searchInput.value.searchPartnerName,
        pageNumber: currentPage.value,
        size: pageSize.value,
    };

    loadingStore.loading = true;
    adminPartnerService.retrieveAllPartnerPaging(paramObj)
        .then( (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements
                list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            } else {

            }
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};
// 검색 '초기화' 버튼
const clearSearchInput = () => {
    _searchInput.value = {
        searchPartnerName: '',  // 담당기관명
    };
};

// 검색 '검색' 버튼
const onClickSearch = () => {
    currentPage.value = 1;
    retrieveList();
};

// 페이지 변경
const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
};

// 페이지 사이즈 변경
const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
};

</script>

<template>
    <div class="container">
        <div class="flex-wrap admin">
            <SNBAdmin class="snb" />
            <div class="content">
                <div class="sub-title">
                    <h3>담당기관 관리</h3>
                </div>
                <!-- s: 검색 -->
                <div class="filter-wrap" :class="{'open': searchOpen}">
                    <p @click="searchOpen = !searchOpen">검색</p>
                    <div class="filter">
                        <dl>
                            <dt>담당기관명</dt>
                            <dd>
                                <KSAInput class="text-box" v-model="_searchInput.searchPartnerName"
                                        placeholder="담당기관명을 입력해 주세요." />
                            </dd>
                        </dl>
                        <div class="btn-wrap">
                            <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearchInput" />
                            <KSAPrimaryButton text="검색" @click="onClickSearch" />
                        </div>
                    </div>
                </div>
                <!-- e: 검색 -->
                <!-- s: 버튼영역 -->
                <div class="list-top">
                    <KSAPrimaryButton text="담당기관 등록" @click="router.push({ name:'PartnerCreate' })" />
                </div>
                <div class="list-top">
                    <p>전체 <span>{{ $number(totalCount) }}</span>건</p>
                    <div class="grid">
                        <KSASelect ref="searchPageSizeRef" :options="pageSizeGroup" class="select ml-xs" @selectOption="onSelectPageSize" />
                    </div>
                </div>
                <!-- e: 버튼영역 -->
                <!-- s: 담당기관 리스트 -->
                <div class="table-form">
                    <table class="horizontal" v-if="list !== null">
                        <tr>
                            <th>NO</th>
                            <th>담당기관ID</th>
                            <th>담당기관명</th>
                            <th>관리자수</th>
                            <th>담당기간</th>
                            <th>최근 수정일</th>
                            <th>최초 등록일</th>
                        </tr>
                        <tr v-for="partner in list">
                            <td>{{ partner.boardNo }}</td>
                            <td>{{ partner.partnerId }}</td>
                            <td class="clickable blue">
                                <RouterLink :to="{name:'PartnerDetail', params:{id: partner.partnerId}}">{{partner.partnerName}}</RouterLink>
                            </td>
                            <td>{{ partner.partnerUserCnt }}</td>
                            <td>{{ $yyyymmdd(partner.partnerStartYmd) }} ~ {{ $yyyymmdd(partner.partnerEndYmd) }}</td>
                            <td>{{ $yyyymmddHHmmss(partner.modifyDate) }}</td>
                            <td>{{ $yyyymmddHHmmss(partner.createDate) }}</td>
                        </tr>
                    </table>
                    <div class="result-none" v-if="list.length === 0">
                        등록된 담당기관이 없습니다.
                    </div>
                </div>
                <Paginate
                    v-if="list.length > 0"
                    v-model="currentPage"
                    :page-count="totalPages"
                    :click-handler="onSelectPage"
                />
                <!-- e: 담당기관 리스트 -->
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";
.blue {
  color: $blue_color;
}
.table-form {
  table {
    &.vertical {
      &.align-left {
        th {
          text-align: left;
          span {
            display: block;
            font-size: $font-size_xs;
            color: $gray_color;
            font-weight: normal;
          }
        }
      }
    }
  }
  .clickable {
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>